// StatusChart.jsx — candidates by current status, as horizontal bars split by service
// line. Statuses are grouped like the workbook's "Pivot Carina" sheet (Screening,
// Ongoing, Hired, Abandoned, On hold). Every status keeps its row, at zero when a
// filter leaves it empty, so the labels never move.
// Selecting a bar lists that status's candidates below. The legend chips switch each
// service line's segment on or off, so lines can be compared or added together.

import { useState } from 'react';
import PropTypes from 'prop-types';

import { SERVICE_LINES, STATUSES, statusInfo, statusNumber } from '@/utils/recruitment/recruitment';

import { recruitmentCss as css } from '@digital_monorepo/styling';

// Fixed per service line (validated with the dataviz palette check), so a filter never repaints a line.
export const SERVICE_LINE_COLOURS = { TC: '#1a7f4e', BC: '#86bc25', TO: '#007cb0' };

const tally = candidates => {
  const counts = { total: candidates.length };
  candidates.forEach(c => { counts[c.ServiceLine] = (counts[c.ServiceLine] || 0) + 1; });
  return counts;
};

const breakdown = (counts, lines) => lines.map(l => `${l} ${counts[l] || 0}`).join(' · ');

const sumOf = (counts, lines) => lines.reduce((sum, l) => sum + (counts[l] || 0), 0);

const StatusChart = ({ candidates, serviceLine, selectedKey, onSelect }) => {
  const [hidden, setHidden] = useState([]);
  const lines = serviceLine ? [serviceLine] : SERVICE_LINES;
  // A single-line filter above always shows that line, whatever was toggled off.
  const shown = serviceLine ? lines : lines.filter(l => !hidden.includes(l));

  // The last visible line cannot be switched off, so the chart is never empty.
  const toggle = line => setHidden(prev => (prev.includes(line)
    ? prev.filter(l => l !== line)
    : shown.length > 1 ? [...prev, line] : prev));

  const byStatus = new Map();
  candidates.forEach(c => {
    if (!byStatus.has(c.Status)) byStatus.set(c.Status, []);
    byStatus.get(c.Status).push(c);
  });

  // Statuses missing from the LOV list are still shown, after the known ones.
  const known = new Set(STATUSES.map(s => s.key));
  const extra = [...byStatus.keys()].filter(k => !known.has(k)).map(statusInfo);
  const groups = [];
  [...STATUSES, ...extra].forEach(s => {
    let group = groups.find(g => g.name === s.group);
    if (!group) groups.push(group = { name: s.group, statuses: [] });
    group.statuses.push({ ...s, counts: tally(byStatus.get(s.key) || []) });
  });

  const grand = tally(candidates);
  const grandShown = sumOf(grand, shown);
  const max = Math.max(1, ...groups.flatMap(g => g.statuses.map(s => sumOf(s.counts, shown))));

  return (
    <div className={`${css.chartCard} ${css.statusCard}`}>
      <div className={css.chartCardHeader}>
        <div>
          <h3 className={css.chartCardTitle}>Candidates by status</h3>
          <p className={css.chartHint}>Select a status to list its candidates below.</p>
        </div>
        <ul className={css.legend} aria-label='Service lines shown in the bars'>
          {lines.map(l => {
            const on = shown.includes(l);
            return (
              <li key={l}>
                <button
                  type='button'
                  className={`${css.legendToggle} ${on ? '' : css.legendToggleOff}`}
                  aria-pressed={on}
                  disabled={Boolean(serviceLine)}
                  title={serviceLine ? undefined : on ? `Hide ${l}` : `Show ${l}`}
                  onClick={() => toggle(l)}>
                  <span className={css.legendSwatch} style={{ background: on ? SERVICE_LINE_COLOURS[l] : 'transparent', borderColor: SERVICE_LINE_COLOURS[l] }} aria-hidden='true' />
                  {l}
                  <strong>{grand[l] || 0}</strong>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={css.barBody}>
        {groups.map(group => {
          const groupTotal = group.statuses.reduce((sum, s) => sum + sumOf(s.counts, shown), 0);
          return (
            <div key={group.name} className={css.barGroup} style={{ flexGrow: group.statuses.length + 1 }} role='group' aria-label={`${group.name}: ${groupTotal}`}>
              <div className={css.barGroupHeader}>
                <span>{group.name}</span>
                <span>{groupTotal}</span>
              </div>
              {group.statuses.map(s => {
                const selected = selectedKey === s.key;
                const value = sumOf(s.counts, shown);
                return (
                  <button
                    key={s.key}
                    type='button'
                    className={`${css.barRow} ${selected ? css.barRowSelected : ''} ${s.counts.total ? '' : css.barRowEmpty}`}
                    aria-disabled={!s.counts.total && !selected}
                    aria-pressed={selected}
                    aria-label={`${s.label}: ${value} (${breakdown(s.counts, shown)})`}
                    onClick={() => (s.counts.total || selected) && onSelect(selected ? null : s.key)}>
                    <span className={css.barLabel}>
                      <span className={css.statusNumber}>{statusNumber(s.key)}</span>
                      <span className={css.barLabelText}>{s.label}</span>
                    </span>
                    <span className={css.barTrack}>
                      <span className={css.barFill} style={{ width: `${(value / max) * 100}%` }}>
                        {shown.filter(l => s.counts[l]).map(l => (
                          <span key={l} className={css.barSegment} style={{ flexGrow: s.counts[l], background: SERVICE_LINE_COLOURS[l] }} />
                        ))}
                      </span>
                      <span className={css.barTooltip} aria-hidden='true'>
                        <strong>{s.label}</strong>
                        {lines.map(l => (
                          <span key={l} className={`${css.barTooltipRow} ${shown.includes(l) ? '' : css.barTooltipRowOff}`}>
                            <span className={css.legendSwatch} style={{ background: SERVICE_LINE_COLOURS[l] }} />
                            {l}<b>{s.counts[l] || 0}</b>
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className={css.barValue}>
                      {value}
                      <span className={css.barShare}>{grandShown ? Math.round((value / grandShown) * 100) : 0}%</span>
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

StatusChart.propTypes = {
  candidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  serviceLine: PropTypes.string,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

export default StatusChart;
